/**
 * Toast Provider
 *
 * Thin wrapper that delegates to sonner's toast() for rendering.
 * Keeps the same useToast() API so existing call sites need no changes.
 * The value never changes, so a component may keep it in an effect's
 * dependencies without the effect running again.
 */

import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { ToastContext, type ToastContextType, type ToastOptions, type ToastType } from './toastContextDef';

export { ToastContext, type ToastType, type ToastContextType, type ToastOptions } from './toastContextDef';

interface ToastProviderProps {
  children: ReactNode;
}

const DEFAULT_DURATION = 5000;

/**
 * Every call reaches sonner on the next tick, in the order it was made. The
 * <Toaster /> is mounted beside the page (router.tsx) and only listens once its
 * own effect has run, which is after the page's: a toast sent while the page
 * mounts (the top-up return toast) would otherwise be dropped. Dismisses wait
 * in the same queue, so one never overtakes the toast it dismisses.
 */
const later = (run: () => void) => {
  setTimeout(run, 0);
};

const addToast = (type: ToastType, message: string, duration = DEFAULT_DURATION, options: ToastOptions = {}) => {
  const opts = options.id === undefined ? { duration } : { duration, id: options.id };
  later(() => {
    if (type === 'success') toast.success(message, opts);
    else if (type === 'error') toast.error(message, opts);
    else if (type === 'warning') toast.warning(message, opts);
    else toast.info(message, opts);
  });
};

const TOASTS: ToastContextType = {
  addToast,
  success: (message, duration, options) => addToast('success', message, duration, options),
  error:   (message, duration, options) => addToast('error',   message, duration, options),
  warning: (message, duration, options) => addToast('warning', message, duration, options),
  info:    (message, duration, options) => addToast('info',    message, duration, options),
  dismiss: (id) => {
    later(() => toast.dismiss(id));
  },
};

export const ToastProvider = ({ children }: ToastProviderProps) => (
  <ToastContext value={TOASTS}>
    {children}
  </ToastContext>
);
